import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  message,
  Image,
  Space,
  Tag,
  Descriptions,
  Spin,
  Modal,
  Switch,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { productService } from '../services/productService';
import { commonAttachFileService } from '../services/commonAttachFileService';
import type { Product, SpecDetail, I18nData, CommonAttachFile } from '../types/product';
import {
  getCategoryLabel,
  getChildCategoryLabel,
  pickChildCategoryFields,
} from '../constants/productCategory';
import { LANG_LIST, LangBlock, LangKey, TextLine } from '../components/I18nDetailBlocks';
import { useLanguage } from '../contexts/LanguageContext';

interface LangAssets {
  logoUrl?: string;
  detailImages: CommonAttachFile[];
  showImages: CommonAttachFile[];
}

const ImageGrid: React.FC<{ images: CommonAttachFile[]; altPrefix: string }> = ({
  images,
  altPrefix,
}) => {
  if (!images.length) {
    return <span style={{ color: '#bbb', fontSize: 13 }}>暂无图片</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
      {images.map((image, index) => (
        <div
          key={image.id ?? index}
          style={{
            width: 100,
            height: 100,
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          <Image
            width="100%"
            height="100%"
            src={image.fullUrl}
            alt={`${altPrefix}${index + 1}`}
            style={{ objectFit: 'cover' }}
          />
        </div>
      ))}
    </div>
  );
};

const ProductDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [langAssets, setLangAssets] = useState<Record<LangKey, LangAssets>>({
    zh: { detailImages: [], showImages: [] },
    en: { detailImages: [], showImages: [] },
    pl: { detailImages: [], showImages: [] },
  });

  useEffect(() => {
    if (id) {
      fetchProductDetail(id);
    }
  }, [id]);

  const loadMultilingualAssets = async (productData: Product) => {
    setImagesLoading(true);
    const assets: Record<LangKey, LangAssets> = {
      zh: { detailImages: [], showImages: [] },
      en: { detailImages: [], showImages: [] },
      pl: { detailImages: [], showImages: [] },
    };

    for (const lang of LANG_LIST) {
      const i18nData = productData.i18n?.[lang.key];
      let logoUrl = i18nData?.logoUrl;
      let detailCaseNo = i18nData?.detailCaseNo;
      let showCaseNo = i18nData?.showCaseNo;

      if (lang.key === 'zh') {
        logoUrl = logoUrl || productData.logoUrl;
        detailCaseNo = detailCaseNo || productData.detailCaseNo;
        showCaseNo = showCaseNo || productData.showCaseNo;
      }

      let detailImages: CommonAttachFile[] = [];
      let showImages: CommonAttachFile[] = [];

      if (lang.key === 'zh' && !i18nData?.detailCaseNo && productData.detailImageList?.length) {
        detailImages = productData.detailImageList;
      } else if (detailCaseNo) {
        try {
          const res = await commonAttachFileService.listByCaseNo(detailCaseNo);
          if (res?.data && Array.isArray(res.data)) {
            detailImages = res.data;
          }
        } catch {
          /* 忽略单语言图片加载失败 */
        }
      }

      if (lang.key === 'zh' && !i18nData?.showCaseNo && productData.showImageList?.length) {
        showImages = productData.showImageList;
      } else if (showCaseNo) {
        try {
          const res = await commonAttachFileService.listByCaseNo(showCaseNo);
          if (res?.data && Array.isArray(res.data)) {
            showImages = res.data;
          }
        } catch {
          /* 忽略单语言图片加载失败 */
        }
      }

      assets[lang.key] = { logoUrl, detailImages, showImages };
    }

    setLangAssets(assets);
    setImagesLoading(false);
  };

  const fetchProductDetail = async (productId: string) => {
    setLoading(true);
    try {
      const response = await productService.getProductDetail(productId);
      const productData = response.data.shopProduct;
      setProduct(productData);
      await loadMultilingualAssets(productData);
    } catch (error) {
      message.error('获取商品详情失败');
    } finally {
      setLoading(false);
    }
  };

  const getI18nText = (lang: LangKey, field: keyof Pick<I18nData, 'name' | 'remark' | 'specialTips' | 'deliveryTime'>) => {
    if (!product) return '';
    const fromI18n = product.i18n?.[lang]?.[field];
    if (fromI18n) return fromI18n;
    if (lang === 'zh') {
      return (product[field] as string | undefined) || '';
    }
    return '';
  };

  const handleEdit = () => {
    navigate(`/products/edit/${id}`);
  };

  const handleDelete = () => {
    message.info('删除功能待实现');
  };

  const handleToggleStatus = (checked: boolean) => {
    if (!product) return;

    const action = checked ? '上架' : '下架';
    Modal.confirm({
      title: `确认${action}商品`,
      content: `确定要${action}这个商品吗？`,
      onOk: async () => {
        try {
          if (checked) {
            await productService.onlineProduct(id!);
            message.success('商品上架成功');
          } else {
            await productService.offlineProduct(id!);
            message.success('商品下架成功');
          }
          fetchProductDetail(id!);
        } catch (error) {
          message.error('操作失败');
        }
      },
    });
  };

  const handleBack = () => {
    navigate('/products');
  };

  const renderSpecs = () => {
    if (!product?.specDetails?.length) return null;

    const groupedSpecs = product.specDetails.reduce<Record<string, SpecDetail[]>>((acc, spec) => {
      const groupName = spec.parentName || spec.name;
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(spec);
      return acc;
    }, {});

    return (
      <Card title={t('productSpecsTitle')} className="content-card" style={{ marginBottom: 16 }}>
        <div
          data-i18n-ignore="true"
          style={{
            background: '#fafafa',
            padding: 16,
            borderRadius: 6,
            border: '1px solid #f0f0f0',
          }}
        >
          {Object.entries(groupedSpecs).map(([groupName, specs]) => (
            <div key={groupName} style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#666',
                  marginBottom: 10,
                  borderBottom: '1px solid #e8e8e8',
                  paddingBottom: 4,
                }}
              >
                {groupName}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {specs.map((spec) => (
                  <div
                    key={spec.id}
                    style={{
                      background: '#fff',
                      border: '1px solid #e8e8e8',
                      borderRadius: 6,
                      padding: '10px 12px',
                    }}
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'center' }}>
                      <span>
                        <Tag color="red">中文</Tag>
                        {spec.name}
                      </span>
                      {spec.nameEn ? (
                        <span>
                          <Tag color="blue">EN</Tag>
                          {spec.nameEn}
                        </span>
                      ) : (
                        <span style={{ color: '#bbb', fontSize: 13 }}>
                          <Tag color="blue">EN</Tag>暂无
                        </span>
                      )}
                      {spec.namePl ? (
                        <span>
                          <Tag color="gold">PL</Tag>
                          {spec.namePl}
                        </span>
                      ) : (
                        <span style={{ color: '#bbb', fontSize: 13 }}>
                          <Tag color="gold">PL</Tag>暂无
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>商品不存在</p>
        <Button onClick={handleBack}>返回列表</Button>
      </div>
    );
  }

  const childCategoryFields = pickChildCategoryFields(product);

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginRight: 12 }}>
            返回
          </Button>
          <h1 className="page-title" style={{ margin: 0 }}>
            {t('productDetail')}
          </h1>
        </div>
      </div>

      <Card title={t('productInfo')} className="content-card" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('productNameMain')} span={2}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {product.logoUrl && (
                  <Image
                    width={40}
                    height={40}
                    src={product.logoUrl}
                    alt={product.name}
                    style={{ borderRadius: 6, objectFit: 'cover', marginRight: 12 }}
                  />
                )}
                <div>
                  <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{product.name}</div>
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>ID: {product.id}</div>
                </div>
              </div>
              <Space>
                <Switch
                  checked={product.isShow === 1}
                  onChange={handleToggleStatus}
                  checkedChildren="已上架"
                  unCheckedChildren="已下架"
                  style={{
                    backgroundColor: product.isShow === 1 ? '#52c41a' : '#d9d9d9',
                    minWidth: 60,
                  }}
                />
                <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
                  编辑
                </Button>
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                  删除
                </Button>
              </Space>
            </div>
          </Descriptions.Item>

          <Descriptions.Item label={t('fieldProductType')}>
            <Tag color="blue">{getCategoryLabel(product.category, product.categoryDesc)}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t('fieldChildCategory')}>
            {getChildCategoryLabel(childCategoryFields.code, childCategoryFields.desc)}
          </Descriptions.Item>

          <Descriptions.Item label={t('fieldStatus')}>
            <Tag color={product.isShow === 1 ? 'green' : 'red'}>
              {product.statusText || (product.isShow === 1 ? '已上架' : '已下架')}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t('fieldSort')}>{product.seq || 0}</Descriptions.Item>

          <Descriptions.Item label={t('fieldOriginalPrice')}>
            <span style={{ fontSize: 16, fontWeight: 500, color: '#1890ff' }}>¥{product.price || 0}</span>
          </Descriptions.Item>

          <Descriptions.Item label={t('fieldDiscountPrice')}>
            {product.discountPrice ? (
              <span style={{ fontSize: 16, fontWeight: 500, color: '#ff4d4f' }}>¥{product.discountPrice}</span>
            ) : (
              '--'
            )}
          </Descriptions.Item>

          <Descriptions.Item label={t('fieldDeliveryFee')}>
            {product.deliveryFee !== undefined && product.deliveryFee !== null
              ? `${product.deliveryFee} zł`
              : '--'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('multiLangContent')} className="content-card" style={{ marginBottom: 16 }}>
        {LANG_LIST.map((lang) => (
          <LangBlock key={lang.key} lang={lang}>
            <TextLine label={t('fieldProductName')} value={getI18nText(lang.key, 'name')} />
            <TextLine label={t('fieldDeliveryTime')} value={getI18nText(lang.key, 'deliveryTime')} />
            <TextLine label={t('fieldSpecialTips')} value={getI18nText(lang.key, 'specialTips')} />
            <TextLine label={t('fieldProductDesc')} value={getI18nText(lang.key, 'remark')} />
          </LangBlock>
        ))}
      </Card>

      {renderSpecs()}

      <Card title={t('multiLangImages')} className="content-card">
        {imagesLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          LANG_LIST.map((lang) => {
            const assets = langAssets[lang.key];

            return (
              <LangBlock key={lang.key} lang={lang}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: '#666', marginBottom: 6, fontSize: 13 }}>{t('fieldLogo')}</div>
                  {assets.logoUrl ? (
                    <Image
                      width={64}
                      height={64}
                      src={assets.logoUrl}
                      alt={`LOGO ${lang.label}`}
                      style={{ borderRadius: 6, objectFit: 'cover', border: '1px solid #d9d9d9' }}
                    />
                  ) : (
                    <span style={{ color: '#bbb', fontSize: 13 }}>{t('emptyNone')}</span>
                  )}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: '#666', marginBottom: 8, fontSize: 13 }}>{t('fieldDetailImages')}</div>
                  <ImageGrid images={assets.detailImages} altPrefix={`${t('fieldDetailImages')}${lang.label}`} />
                </div>
                <div>
                  <div style={{ color: '#666', marginBottom: 8, fontSize: 13 }}>{t('fieldShowImages')}</div>
                  <ImageGrid images={assets.showImages} altPrefix={`展示图片${lang.label}`} />
                </div>
              </LangBlock>
            );
          })
        )}
      </Card>
    </div>
  );
};

export default ProductDetail;
